import { useCallback, useEffect, useRef, useState } from "react";
import api from "../api";
import { buildCertificateShareLabels } from "./ShareSectionDrawer";
import { formatCertificateDate } from "./CourseCertificateInfographic";
import { capturePosterPng } from "./inlinePosterImages";

const CERTIFICATE_BACKGROUND = "#fcf5e5";

/**
 * Resolves the name printed on the certificate. The profile screen treats
 * givenName/familyName as the original name and displayGivenName/displayFamilyName
 * as the preferred one, so the original wins here and the preferred is the fallback.
 */
export function useCertificateRecipientName(enabled = true) {
  const [recipientName, setRecipientName] = useState("");

  useEffect(() => {
    if (!enabled) return undefined;
    const userEmail = localStorage.getItem("userEmail");
    if (!userEmail) {
      setRecipientName("Anonymous User");
      return undefined;
    }

    let cancelled = false;
    api
      .get("/fetchUserRole", { params: { userEmail } })
      .then((response) => {
        if (cancelled) return;
        const user = response?.data || {};
        const original = [user.givenName, user.familyName]
          .filter(Boolean)
          .join(" ")
          .trim();
        const display = [user.displayGivenName, user.displayFamilyName]
          .filter(Boolean)
          .join(" ")
          .trim();
        setRecipientName(original || display || "Anonymous User");
      })
      .catch(() => {
        if (cancelled) return;
        const fallback = [
          localStorage.getItem("userGivenName"),
          localStorage.getItem("userFamilyName"),
        ]
          .filter(Boolean)
          .join(" ")
          .trim();
        setRecipientName(fallback || "Anonymous User");
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return recipientName;
}

/**
 * Drives the certificate share drawer: builds a job, captures the off-screen
 * certificate as a PNG and hands it to ShareSectionDrawer. Callers render the
 * drawer and the hidden certificate via CertificateShareSurfaces.
 */
export function useCertificateShare() {
  const [shareJob, setShareJob] = useState(null);
  const [drawerExpanded, setDrawerExpanded] = useState(false);
  const requestIdRef = useRef(0);
  const certificateRef = useRef(null);
  const closeTimeoutRef = useRef(null);
  const lastDetailsRef = useRef(null);

  const startShare = useCallback((details) => {
    const {
      recipientName,
      courseCardTitle,
      courseCover,
      givenOn,
    } = details || {};
    lastDetailsRef.current = details || {};

    const requestId = Date.now();
    requestIdRef.current = requestId;
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }

    setShareJob({
      requestId,
      status: "generating",
      labels: buildCertificateShareLabels({ recipientName, courseCardTitle }),
      certificate: {
        recipientName: recipientName || "Anonymous User",
        courseTitle: courseCardTitle,
        courseCover,
        givenOn: givenOn || formatCertificateDate(),
      },
      aspectRatio: "auto",
      imageBase64: null,
      mimeType: null,
      error: null,
    });
    setDrawerExpanded(false);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setDrawerExpanded(true);
      });
    });
  }, []);

  const retryShare = useCallback(() => {
    if (!lastDetailsRef.current) return;
    startShare(lastDetailsRef.current);
  }, [startShare]);

  const closeDrawer = useCallback(() => {
    setDrawerExpanded(false);
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    closeTimeoutRef.current = setTimeout(() => {
      setShareJob(null);
      closeTimeoutRef.current = null;
    }, 360);
  }, []);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (shareJob?.status !== "generating") return undefined;
    const requestId = shareJob.requestId;
    let cancelled = false;

    const capture = async () => {
      if (document.fonts?.ready) {
        await document.fonts.ready;
      }
      await new Promise((resolve) => requestAnimationFrame(resolve));
      await new Promise((resolve) => requestAnimationFrame(resolve));
      const node = certificateRef.current;
      if (!node) {
        throw new Error("Could not build the certificate");
      }
      const dataUrl = await capturePosterPng(node, {
        backgroundColor: CERTIFICATE_BACKGROUND,
      });
      if (cancelled || requestIdRef.current !== requestId) return;
      const imageBase64 = String(dataUrl).split(",")[1] || "";
      setShareJob((prev) =>
        prev && prev.requestId === requestId
          ? {
              ...prev,
              status: "ready",
              imageBase64,
              mimeType: "image/png",
            }
          : prev
      );
    };

    capture().catch((error) => {
      if (cancelled || requestIdRef.current !== requestId) return;
      setShareJob((prev) =>
        prev && prev.requestId === requestId
          ? {
              ...prev,
              status: "error",
              error: error.message || "Could not generate the certificate",
            }
          : prev
      );
    });

    return () => {
      cancelled = true;
    };
  }, [shareJob?.status, shareJob?.requestId]);

  return {
    shareJob,
    drawerExpanded,
    certificateRef,
    startShare,
    retryShare,
    closeDrawer,
  };
}
